import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PageHeader, SectionCard, DataTable, EmptyState, LoadingState, StatusBadge, Pill, ActionButton,
} from "@/components/shared";
import { SalesNav, STATUS_TONES, usd, fmtDate } from "./common";

export default function EstimateFollowUp() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/sales/follow-up"); setRows(data || []); }
    catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remind = async (id) => { try { await api.post(`/sales/proposals/${id}/remind`, { status: "manual", base_url: window.location.origin }); toast.success("Reminder sent"); load(); } catch (e) { toast.error("Failed"); } };

  const columns = [
    { key: "proposal_number", label: "Estimate #" },
    { key: "client_name", label: "Client" },
    { key: "total", label: "Amount", align: "right" },
    { key: "status", label: "Status" },
    { key: "days_since_sent", label: "Days Since Sent", align: "right" },
    { key: "actions", label: "" },
  ];
  const renderCell = (r, c) => {
    switch (c.key) {
      case "proposal_number": return <span className="font-mono text-xs text-slate-500">{r.proposal_number}</span>;
      case "total": return <span className="font-medium">{usd(r.total, r.currency)}</span>;
      case "status": return <StatusBadge status={r.status} map={STATUS_TONES} />;
      case "days_since_sent": return r.needs_followup ? <Pill tone="danger">{r.days_since_sent}d</Pill> : <span className="text-slate-500">{r.days_since_sent}d</span>;
      case "actions": return <div className="flex gap-2 justify-end"><ActionButton variant="ghost" icon="BellRing" onClick={(e) => { e.stopPropagation(); remind(r.id); }} data-testid={`followup-remind-${r.id}`}>Remind</ActionButton></div>;
      default: return <span className="text-slate-700">{r[c.key] || "—"}</span>;
    }
  };

  return (
    <div data-testid="follow-up-page">
      <PageHeader title="Sales & Estimates" description="Open estimates awaiting client action." />
      <SalesNav />
      <SectionCard>
        {loading ? <LoadingState label="Loading follow-up queue…" /> : (
          <DataTable columns={columns} rows={rows} renderCell={renderCell} testId="followup-table"
            empty={<EmptyState icon="BellRing" title="Nothing to follow up" description="All sent estimates are up to date." />} />
        )}
      </SectionCard>
      <RowBinder onRowClick={(id) => navigate(`/sales/proposals/${id}`)} />
    </div>
  );
}

function RowBinder({ onRowClick }) {
  useEffect(() => {
    const table = document.querySelector('[data-testid="followup-table"]');
    if (!table) return;
    const h = (e) => { if (e.target.closest("button")) return; const tr = e.target.closest('[data-testid^="row-"]'); if (tr) onRowClick(tr.getAttribute("data-testid").replace("row-", "")); };
    table.addEventListener("click", h); table.style.cursor = "pointer";
    return () => table.removeEventListener("click", h);
  });
  return null;
}
